import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Tooltip, Modal, Table, Input, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import useGetAllOrganizationsLight from "../../../customHooks/GET_requests/useGetAllOrganizationsLight";
// COMPONENTS
import TableColumnOrganizationType from "../table/TableColumnOrganizationType";
// INTERFACES
import { OganizationLightInterface } from "../../../interfaces/Organizations.interface";
import { SelectOrganizationInterface } from "../../../interfaces/Global.interface";
// FLATBUFFER ENUMS
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";

interface OrganizationRow {
  value: string;
  label: string;
  // `/orgs/light` (OrgLight) only returns id/name/type — no parent
  // reference — so there is no Parent column here; Type is available and
  // shown below.
  type?: OrgType;
}

/**
 * Drop-in replacement for SelectOrganizationsLight used only by the header
 * org switcher (YourProfileDrawer). Operators complained the Select's fixed
 * 200px width made long org names unreadable and the flat option list hard
 * to search — this renders the current selection as a Tag and moves picking
 * into a Modal with a searchable Table, which has room for both.
 *
 * Selection is staged locally (`draftValue`) and only committed via
 * `onChangeIdOrganization` when Save is clicked, so opening the modal and
 * cancelling never touches the caller's state.
 */
function TagSelectOrganizationsLight({
  value,
  onChangeIdOrganization,
  disabledInput,
  filterType,
  withBanks,
  excludeIds,
}: SelectOrganizationInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [draftValue, setDraftValue] = useState<string | undefined>(undefined);

  // ************************************************ */
  // CUSTOM HOOKS ************************************ */
  const { organizationsLight: organizations, isLoadingGetAllOrganizationsLight } =
    useGetAllOrganizationsLight(withBanks);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Filter by type when requested, including MASTER organizations.
  // If the API doesn't return type yet (backend not deployed), the filter
  // produces an empty list — fall back to showing all orgs so the list isn't empty.
  const filterTypeToEnum: Record<string, OrgType> = {
    MASTER: OrgType.MASTER,
    ORG: OrgType.ORG,
    SUBORG: OrgType.SUBORG,
  };
  const byType = filterType
    ? organizations.filter(
        (org: OganizationLightInterface) => org.type === (filterTypeToEnum[filterType] ?? org.type),
      )
    : organizations;
  const filtered = byType.length > 0 ? byType : organizations;

  // Applied AFTER the type filter and the show-all fallback so excluded orgs can
  // never leak back in.
  const excludeSet = new Set(excludeIds ?? []);
  const options: OrganizationRow[] = filtered
    .map((item: OganizationLightInterface) => ({
      value: formatUuidT(item.id),
      label: String(item.name ?? ""),
      type: item.type,
    }))
    .filter((opt) => !excludeSet.has(opt.value));

  const valueExistsInOptions = value && options.some((opt) => opt.value === value);
  const displayValue = valueExistsInOptions ? value : undefined;
  const selectedLabel = options.find((opt) => opt.value === displayValue)?.label;

  const filteredOptions = useMemo(
    () =>
      options.filter((opt) => opt.label.toLowerCase().includes(searchText.trim().toLowerCase())),
    [options, searchText],
  );

  const openModal = () => {
    if (disabledInput) return;
    setDraftValue(displayValue);
    setSearchText("");
    setIsModalOpen(true);
  };

  const onCancel = () => setIsModalOpen(false);

  const onSave = () => {
    if (draftValue) {
      const record = options.find((opt) => opt.value === draftValue);
      onChangeIdOrganization(draftValue, record);
    }
    setIsModalOpen(false);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  // Drop a staged selection that no longer exists in the (possibly refetched)
  // options list, mirroring the same guard applied to `displayValue`.
  useEffect(() => {
    if (draftValue && !options.some((opt) => opt.value === draftValue)) {
      setDraftValue(undefined);
    }
  }, [options]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const typeLabelForSort = (type?: OrgType) => (type !== undefined ? orgTypeLabel(type) : "");

  const columns: ColumnsType<OrganizationRow> = [
    {
      title: t("report.selectOrganization") || "Organization",
      dataIndex: "label",
      key: "label",
      sorter: (a, b) => a.label.localeCompare(b.label),
    },
    {
      title: t("organizationManagement.type") || "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      sorter: (a, b) => typeLabelForSort(a.type).localeCompare(typeLabelForSort(b.type)),
      render: (type?: OrgType) =>
        type !== undefined ? <TableColumnOrganizationType type={orgTypeLabel(type)} /> : "--",
    },
  ];

  return (
    <>
      <Tooltip title={selectedLabel || ""}>
        <Tag
          color="blue"
          className={`generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis ${
            disabledInput ? "" : "generalStyles__hoverCursor"
          }`}
          onClick={openModal}
        >
          {selectedLabel || "--"}
        </Tag>
      </Tooltip>
      <Modal
        title={t("header.selectOrganizationTitle") || "Select organization"}
        open={isModalOpen}
        onCancel={onCancel}
        width={560}
        destroyOnClose
        footer={
          <div className="component__tagSelectLight__footer">
            <Button className="component__tagSelectLight__footerButton" onClick={onCancel}>
              {t("general.cancel")}
            </Button>
            <Button
              type="primary"
              className="component__tagSelectLight__footerButton"
              disabled={!draftValue}
              onClick={onSave}
            >
              {t("general.save")}
            </Button>
          </div>
        }
      >
        <p className="component__tagSelectLight__description">
          {t("header.selectOrganizationDescription")}
        </p>
        <Input.Search
          allowClear
          placeholder={t("general.search") || "Search"}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="component__tagSelectLight__search"
        />
        <Table<OrganizationRow>
          size="small"
          rowKey="value"
          loading={isLoadingGetAllOrganizationsLight}
          columns={columns}
          dataSource={filteredOptions}
          pagination={false}
          scroll={{ y: 320 }}
          rowSelection={{
            type: "radio",
            selectedRowKeys: draftValue ? [draftValue] : [],
            onChange: (keys) => setDraftValue(keys[0] as string),
          }}
          onRow={(record) => ({
            onClick: () => setDraftValue(record.value),
            className: "generalStyles__hoverCursor",
          })}
        />
      </Modal>
    </>
  );
}

export default TagSelectOrganizationsLight;
